<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { selectedCountry } from '../countryStore';
  import { t } from '../i18nStore';

  export let showModal = false;
  export let preSelectedMethods = [];

  const dispatch = createEventDispatcher();

  let allMethods = [];
  let filteredMethods = [];
  let selectedMethods = []; 
  let searchQuery = '';
  let customMethodName = ''; // Custom input

  let isLoading = true;
  let error = '';
  let wasOpen = false;

  // Initialization logic (Fixed to run once per open)
  $: if (showModal) {
    if (!wasOpen) {
      wasOpen = true;
      initialize();
    }
  } else {
    wasOpen = false;
  }

  function initialize() {
    selectedMethods = [...preSelectedMethods];
    searchQuery = '';
    customMethodName = '';
    fetchPaymentMethods();
  }

  async function fetchPaymentMethods() {
    isLoading = true;
    error = '';
    try {
      const response = await fetch(getApiUrl(`/payment-methods?countryCode=${$selectedCountry}`));
      if (!response.ok) throw new Error('Could not load payment methods.');
      allMethods = await response.json();
      filterMethods();
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  $: if (searchQuery || allMethods.length > 0) {
    filterMethods();
  }

  function filterMethods() {
    if (!searchQuery) {
      filteredMethods = allMethods;
    } else {
      const lowercasedQuery = searchQuery.toLowerCase();
      filteredMethods = allMethods.filter(method => 
        method.name.toLowerCase().includes(lowercasedQuery)
      );
    }
  }

  // --- NEW: Custom Logic ---
  function addCustomMethod() {
    const trimmed = customMethodName.trim();
    if (trimmed && !selectedMethods.includes(trimmed)) {
        selectedMethods = [...selectedMethods, trimmed];
        customMethodName = '';
    }
  }

  function removeMethod(name) {
      selectedMethods = selectedMethods.filter(m => m !== name);
  }

  function confirmSelection() {
    dispatch('confirm', { selected: selectedMethods });
    closeModal();
  }

  function closeModal() {
    dispatch('close');
  }
</script>


{#if showModal}
  <div class="modal-backdrop" on:click={closeModal}>
    <div class="modal-content" on:click|stopPropagation>
      <div class="modal-header">
        <h3>{$t('payment_modal.title')} ({$selectedCountry})</h3>
        <button class="close-btn" on:click={closeModal}>&times;</button>
      </div>
      <div class="search-section">
        <input type="text" placeholder={$t('payment_modal.search_placeholder')} bind:value={searchQuery}>
      </div>

      {#if isLoading}
        <p>{$t('payment_modal.loading')}</p>
      {:else if error}
        <p class="error">{error}</p>
      {:else}
        <div class="method-list">
            {#if filteredMethods.length > 0}
                {#each filteredMethods as method (method._id)}
                    <!-- Changed from div to label for better interaction -->
                    <label class="method-item" class:is-selected={selectedMethods.includes(method.name)}>
                      <input type="checkbox" value={method.name} bind:group={selectedMethods}>
                      <span class="method-label">{method.name}</span>
                    </label>
                {/each}
            {:else}
                <p class="no-data">{$t('payment_modal.no_data')}</p>
            {/if}
        </div>
      {/if}

      <div class="custom-section">
          <label for="custom-method">{$t('payment_modal.add_custom_label')}</label>
          <div class="custom-row">
              <input type="text" id="custom-method" bind:value={customMethodName} placeholder={$t('payment_modal.add_custom_placeholder')} on:keydown={(e) => e.key === 'Enter' && addCustomMethod()} />
              <button class="add-btn" on:click={addCustomMethod} disabled={!customMethodName.trim()}>{$t('payment_modal.add_btn')}</button>
          </div>
      </div>

      <div class="selection-summary">
          <strong>{$t('payment_modal.selected_count')} ({selectedMethods.length}):</strong>
          <div class="tags">
              {#each selectedMethods as method}
                  <span class="tag">
                      {method} 
                      <button class="remove-tag" on:click={() => removeMethod(method)}>&times;</button>
                  </span>
              {/each}
          </div>
      </div>

      <div class="modal-actions">
        <button class="secondary" on:click={closeModal}>{$t('payment_modal.cancel_btn')}</button>
        <button class="primary" on:click={confirmSelection}>{$t('payment_modal.confirm_btn')}</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    background: rgba(0, 0, 0, 0.5); display: flex;
    justify-content: center; align-items: center; z-index: 1000;
  }
  .modal-content {
    background: white; padding: 1.5rem; border-radius: 8px;
    width: 90%; max-width: 500px; max-height: 80vh;
    display: flex; flex-direction: column;
  }

  .modal-header {
    padding: 1rem 1.5rem; border-bottom: 1px solid #eee;
    display: flex; justify-content: space-between; align-items: center;
  }
  .modal-header h3 { margin: 0; font-size: 1.2rem; }
  .close-btn { background: none; border: none; font-size: 1.8rem; cursor: pointer; color: #999; }

  h3 { margin-top: 0; }
  .search-section { margin-bottom: 1rem; }
  input[type="text"] {
    width: 100%; padding: 0.5rem; font-size: 1rem;
    border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box;
  }
  .method-list {
    flex-grow: 1; overflow-y: auto; border: 1px solid #eee;
    padding: 1rem; border-radius: 4px; margin-bottom: 1rem; min-height: 100px;
  }
  
  /* Make label behave nicely as a wrapper */
  .method-item { 
    display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 0; border-bottom: 1px solid #f9f9f9; 
    cursor: pointer;
  }
  input[type="checkbox"] { width: 1.2em; height: 1.2em; }
  
  /* Custom Input Styles */
  .custom-section { border-top: 1px solid #eee; padding-top: 1rem; margin-bottom: 1rem; }
  .custom-section label { display: block; font-weight: 500; margin-bottom: 0.5rem; font-size: 0.9rem; }
  .custom-row { display: flex; gap: 0.5rem; }
  .add-btn { background-color: #28a745; color: white; border: none; padding: 0.5rem 1rem; border-radius: 4px; cursor: pointer; }
  .add-btn:disabled { background-color: #ccc; cursor: not-allowed; }

  /* Tags Styles */
  .selection-summary { margin-bottom: 1rem; font-size: 0.9rem; }
  .tags { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.5rem; max-height: 100px; overflow-y: auto; }
  .tag { background-color: #e9ecef; padding: 2px 8px; border-radius: 12px; display: flex; align-items: center; gap: 5px; font-size: 0.85rem; }
  .remove-tag { background: none; border: none; color: #dc3545; font-weight: bold; cursor: pointer; padding: 0; font-size: 1rem; line-height: 1; }

  .modal-actions { display: flex; justify-content: flex-end; gap: 1rem; border-top: 1px solid #eee; padding-top: 1rem; }
  .modal-actions button { padding: 0.6rem 1.2rem; border-radius: 4px; border: none; cursor: pointer; font-size: 1rem; }
  .primary { background-color: #007bff; color: white; }
  .secondary { background-color: #6c757d; color: white; }
  .error { color: red; }
  .no-data { color: #666; font-style: italic; text-align: center; }

  /* --- Mobile Responsive Rules --- */
  @media (max-width: 600px) {
    /* Hide Search, Custom Section, and Summary on Mobile */
    .search-section,
    .custom-section,
    .selection-summary {
      display: none;
    }
    
    /* Transform list into a grid of toggle buttons */
    .method-list {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0.5rem;
      padding: 0.5rem;
    }

    .method-item {
      border: 1px solid #ccc;
      border-radius: 6px;
      padding: 0.75rem 0.5rem;
      justify-content: center;
      text-align: center;
      background-color: #f8f9fa;
      transition: all 0.2s ease-in-out;
      margin: 0; 
      border-bottom: 1px solid #ccc; /* Override desktop list style */
    }

    /* Hide the default checkbox on mobile */
    .method-item input[type="checkbox"] {
      display: none;
    }

    /* Selected state styling for mobile toggle buttons */
    .method-item.is-selected {
      background-color: #007bff;
      border-color: #007bff;
      color: white;
    }

    .method-label {
      font-size: 0.9rem;
      font-weight: 500;
    }
  }
</style>